import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Package, 
  Building2, 
  DollarSign, 
  Calendar, 
  FileText, 
  Hash, 
  Edit,
  ShoppingCart,
  Truck,
  Receipt,
  User,
  MapPin,
  Phone,
  Mail,
  Clock,
  Tag,
  Info,
  Upload,
  Eye,
  Download,
  X,
  List,
  Boxes
} from 'lucide-react';
import { entriesService } from '../services/entriesService';
import { productsService } from '../services/productsService';
import { suppliersService } from '../services/suppliersService';
import { imagesService } from '../services/imagesService';
import DocumentUploader from '../components/DocumentUploader';

const EntryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const page = (() => {
    const p = new URLSearchParams(location.search).get('page');
    return p || sessionStorage.getItem('entries_page') || '1';
  })();
  
  const [entry, setEntry] = useState(null);
  const [products, setProducts] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Se elimina soporte de eliminación según solicitud
  const [showDocumentUploader, setShowDocumentUploader] = useState(false);
  const [showInvoiceUploader, setShowInvoiceUploader] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showInvoiceViewer, setShowInvoiceViewer] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(null);
  // Estado para edición inline (solo campos generales de la entrada)
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ invoice_number: '', notes: '' });

  // Función para formatear dinero
  const formatMoney = (amount) => {
    if (!amount) return '$0.00 MXN';
    
    const number = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(number)) return '$0.00 MXN';
    
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number) + ' MXN';
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'No especificada';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    
    // Ajustar por zona horaria para mostrar la fecha correcta
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() + userTimezoneOffset);
    
    return new Intl.DateTimeFormat('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    }).format(localDate);
  };

  // Función para formatear fecha y hora
  const formatDateTime = (dateString) => {
    if (!dateString) return 'No especificada';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    
    return new Intl.DateTimeFormat('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  useEffect(() => {
    const loadEntryDetails = async () => {
      try {
        setLoading(true);
        console.log('🔄 Cargando detalles de entrada ID:', id);

        // Cargar datos de la entrada
        const entryData = await entriesService.getById(id);
        console.log('📦 Datos de entrada:', entryData);
        setEntry(entryData);
        // Inicializar formulario de edición
        setEditForm({
          invoice_number: entryData.invoice_number || '',
          notes: entryData.notes || ''
        });

        // En el modelo de multi-productos, los productos vienen en el arreglo 'products'
        if (entryData.products && Array.isArray(entryData.products)) {
          setProducts(entryData.products);
          console.log('📦 Productos de la entrada:', entryData.products.length);
        } else {
          // Esto es para compatibilidad con el modelo antiguo
          if (entryData.product_id) {
            try {
              const productData = await productsService.getById(entryData.product_id);
              console.log('🏷️ Datos del producto:', productData);
              setProducts([{
                product_id: entryData.product_id,
                quantity: entryData.quantity,
                unit_cost: entryData.unit_cost,
                total_cost: entryData.total_cost,
                product_name: productData.name,
                product_sku: productData.sku,
                product_unit: productData.unit
              }]);
            } catch (productError) {
              console.warn('⚠️ Error cargando producto:', productError);
            }
          }
        }

        // Cargar datos del proveedor si existe
        if (entryData.supplier_id) {
          try {
            const supplierData = await suppliersService.getById(entryData.supplier_id);
            console.log('🏢 Datos del proveedor:', supplierData);
            setSupplier(supplierData);
          } catch (supplierError) {
            console.warn('⚠️ Error cargando proveedor:', supplierError);
          }
        }

      } catch (error) {
        console.error('❌ Error cargando detalles de entrada:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadEntryDetails();
    }
  }, [id]);

  const handleEdit = () => {
    setIsEditing(prev => !prev);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        invoice_number: editForm.invoice_number || null,
        notes: editForm.notes || null,
        // Enviar campos inmutables para evitar validaciones del backend
        supplier_id: entry?.supplier_id || null,
        entry_date: entry?.entry_date || null
        // No se modifican productos aquí; edición de items múltiples requiere lógica adicional
      };
      const updated = await entriesService.update(id, payload);
      setEntry(updated);
      setIsEditing(false);
      setEditForm({
        invoice_number: updated.invoice_number || '',
        notes: updated.notes || ''
      });
    } catch (e) {
      console.error('❌ Error guardando edición:', e);
      setError(e.message);
    }
  };

  const handleGoBack = () => {
    navigate(`/entries?page=${page}`);
  };

  // Funciones para manejar documentos
  const handleDocumentUpload = async (filename) => {
    try {
      const updatedEntry = await entriesService.updateDocument(id, filename);
      setEntry(updatedEntry);
      setShowDocumentUploader(false);
    } catch (error) {
      console.error('Error actualizando documento:', error);
      setError(error.message);
    }
  };

  const handleInvoiceUpload = async (filename) => {
    try {
      const updatedEntry = await entriesService.updateInvoice(id, filename);
      setEntry(updatedEntry);
      setShowInvoiceUploader(false);
    } catch (error) {
      console.error('Error actualizando factura:', error);
      setError(error.message);
    }
  };

  const handleViewDocument = () => {
    if (entry.requisition_document) {
      if (/\.(pdf)$/i.test(entry.requisition_document)) {
        const url = imagesService.getImageUrl(entry.requisition_document);
        window.open(url, '_blank');
      } else {
        setShowImageViewer(true);
      }
    }
  };

  const handleViewInvoice = () => {
    if (entry.invoice_document) {
      if (/\.(pdf)$/i.test(entry.invoice_document)) {
        const url = imagesService.getImageUrl(entry.invoice_document);
        window.open(url, '_blank');
      } else {
        setShowInvoiceViewer(true);
      }
    }
  };

  const handleViewProduct = (productId) => {
    navigate(`/inventory/product/${productId}`);
  };

  const handleDownloadDocument = async () => {
    if (entry.requisition_document) {
      try {
        const url = imagesService.getImageUrl(entry.requisition_document);
        const response = await fetch(url);
        const blob = await response.blob();
        
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = entry.requisition_document;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Error descargando documento:', error);
      }
    }
  };

  const handleDownloadInvoice = async () => {
    if (entry.invoice_document) {
      try {
        const url = imagesService.getImageUrl(entry.invoice_document);
        const response = await fetch(url);
        const blob = await response.blob();
        
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = entry.invoice_document;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Error descargando factura:', error);
      }
    }
  };

  if (loading) {
    return (
      <motion.div
        className="min-h-[calc(100vh-180px)] px-4 py-8 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Cargando detalles de la entrada...</p>
        </div>
      </motion.div>
    );
  }

  if (error || !entry) {
    return (
      <motion.div
        className="min-h-[calc(100vh-180px)] px-4 py-8 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-700 mb-4">{error || 'Entrada no encontrada'}</p>
          <button 
            onClick={handleGoBack}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver a Entradas
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="min-h-[calc(100vh-180px)] px-4 py-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <motion.button
            onClick={handleGoBack}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </motion.button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Detalle de Entrada #{entry.id}
            </h1>
            <p className="text-gray-600 mt-1">
              Información completa de la entrada al almacén
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <motion.button
            onClick={handleEdit}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isEditing ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Edit className="w-4 h-4" />
            {isEditing ? 'Cancelar' : 'Editar'}
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Información Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Datos de la Entrada */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Información de la Entrada</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Hash className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">ID de Entrada</p>
                    <p className="text-lg font-semibold text-gray-800">#{entry.id}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <ShoppingCart className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Productos</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {products.length} productos diferentes
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Valor Total</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatMoney(entry.total_cost || products.reduce((sum, p) => sum + parseFloat(p.total_cost || 0), 0))}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Fecha de Entrada</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {formatDate(entry.entry_date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500">Número de Factura</p>
                    {!isEditing ? (
                      <p className="text-lg font-semibold text-gray-800">
                        {entry.invoice_number || 'No especificado'}
                      </p>
                    ) : (
                      <input
                        type="text"
                        value={editForm.invoice_number}
                        onChange={e => setEditForm(f => ({ ...f, invoice_number: e.target.value }))}
                        className="mt-1 w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Número de factura"
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Fecha de Registro</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {formatDateTime(entry.created_at)}
                    </p>
                  </div>
                </div>

                {entry.batch_id && (
                  <div className="flex items-start gap-3">
                    <Tag className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">ID del Lote</p>
                      <p className="text-lg font-semibold text-purple-600">
                        {entry.batch_id}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-gray-400 mt-1" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500 mb-2">Notas</p>
                  {!isEditing ? (
                    entry.notes ? (
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{entry.notes}</p>
                    ) : (
                      <p className="text-gray-500 italic">Sin notas</p>
                    )
                  ) : (
                    <textarea
                      rows={3}
                      value={editForm.notes}
                      onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-y"
                      placeholder="Agregar notas..."
                    />
                  )}
                </div>
              </div>
            </div>

            {isEditing && (
              <div className="mt-6 border-t border-dashed pt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Edit className="w-4 h-4" /> Guardar Cambios
                </h3>
                <div className="flex gap-3">
                  <motion.button
                    onClick={handleSaveEdit}
                    className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Guardar
                  </motion.button>
                  <motion.button
                    onClick={() => {
                      setIsEditing(false);
                      setEditForm({
                        invoice_number: entry.invoice_number || '',
                        notes: entry.notes || ''
                      });
                    }}
                    className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Cancelar
                  </motion.button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Lista de Productos */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Boxes className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Productos en esta Entrada</h2>
            </div>

            {products.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">No hay productos en esta entrada</p>
              </div>
            ) : (
              <div className="space-y-4">
                {products.map((product, index) => (
                  <motion.div
                    key={index}
                    className={`border rounded-xl p-4 ${selectedProductIndex === index ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'}`}
                    whileHover={{ scale: 1.01 }}
                    onClick={() => setSelectedProductIndex(selectedProductIndex === index ? null : index)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800">{product.product_name}</h3>
                          <p className="text-sm text-gray-500">SKU: {product.product_sku}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-800">
                          {product.quantity} {product.product_unit || 'unidades'}
                        </p>
                        <p className="text-sm text-green-600">{formatMoney(product.total_cost)}</p>
                      </div>
                    </div>

                    {selectedProductIndex === index && (
                      <motion.div
                        className="mt-4 pt-4 border-t border-gray-200"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div>
                              <p className="text-sm text-gray-500">Precio Unitario</p>
                              <p className="font-medium text-gray-800">{formatMoney(product.unit_cost)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Cantidad</p>
                              <p className="font-medium text-gray-800">{product.quantity} {product.product_unit || 'unidades'}</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <p className="text-sm text-gray-500">Valor Total</p>
                              <p className="font-medium text-gray-800">{formatMoney(product.total_cost)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">ID de Producto</p>
                              <p className="font-medium text-gray-800">{product.product_id}</p>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4">
                          <motion.button
                            onClick={() => handleViewProduct(product.product_id)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Eye className="w-4 h-4" />
                            Ver Ficha Técnica del Producto
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Información del Proveedor y Resumen */}
        <div className="space-y-6">
          {supplier && (
            <motion.div
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Proveedor</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Nombre de la Empresa</p>
                  <p className="text-lg font-semibold text-gray-800">{supplier.name}</p>
                </div>

                {supplier.contact_name && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Contacto</p>
                      <p className="text-lg font-semibold text-gray-800">{supplier.contact_name}</p>
                    </div>
                  </div>
                )}

                {supplier.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Teléfono</p>
                      <p className="text-lg font-semibold text-gray-800">{supplier.phone}</p>
                    </div>
                  </div>
                )}

                {supplier.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="text-lg font-semibold text-blue-600">{supplier.email}</p>
                    </div>
                  </div>
                )}

                {supplier.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Dirección</p>
                      <p className="text-gray-700">{supplier.address}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <motion.button
                  onClick={() => navigate(`/suppliers/supplier/${supplier.id}`)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Building2 className="w-4 h-4" />
                  Ver Perfil del Proveedor
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Resumen de la Entrada */}
          <motion.div
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-lg p-6 border border-blue-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Resumen de la Entrada</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-blue-700">Total de Productos:</span>
                <span className="font-semibold text-blue-800">
                  {products.length} tipos
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-blue-700">Total de Unidades:</span>
                <span className="font-semibold text-blue-800">
                  {products.reduce((sum, p) => sum + (parseInt(p.quantity) || 0), 0)} unidades
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-blue-700">Valor Total:</span>
                <span className="font-bold text-blue-800 text-lg">
                  {formatMoney(entry.total_cost || products.reduce((sum, p) => sum + parseFloat(p.total_cost || 0), 0))}
                </span>
              </div>

              {entry.is_batch_entry && (
                <div className="mt-4 p-3 bg-purple-100 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">
                      Entrada por Lotes
                    </span>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Sección de Documento de Requisición */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Documento de Requisición</h2>
              </div>
              
              <motion.button
                onClick={() => setShowDocumentUploader(true)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Upload className="w-4 h-4" />
                {entry.requisition_document ? 'Cambiar' : 'Subir'} Documento
              </motion.button>
            </div>

            {entry.requisition_document ? (
              <div className="relative group">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {/\.(pdf)$/i.test(entry.requisition_document) ? 'Documento PDF' : 'Imagen de Requisición'}
                        </p>
                        <p className="text-sm text-gray-600 break-all">{entry.requisition_document}</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <motion.button
                        onClick={handleViewDocument}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Ver documento"
                      >
                        <Eye className="w-4 h-4" />
                        Ver
                      </motion.button>
                      
                      <motion.button
                        onClick={handleDownloadDocument}
                        className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Descargar documento"
                      >
                        <Download className="w-4 h-4" />
                        Descargar
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">No hay documento de requisición</p>
                <p className="text-sm">Haz clic en "Subir Documento" para agregar uno</p>
              </div>
            )}
          </motion.div>

          {/* Sección de Factura */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Receipt className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Factura del Material</h2>
              </div>
              
              <motion.button
                onClick={() => setShowInvoiceUploader(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Upload className="w-4 h-4" />
                {entry.invoice_document ? 'Cambiar' : 'Subir'} Factura
              </motion.button>
            </div>

            {entry.invoice_document ? (
              <div className="relative group">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Receipt className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {/\.(pdf)$/i.test(entry.invoice_document) ? 'Documento PDF' : 'Imagen de Factura'}
                        </p>
                        <p className="text-sm text-gray-600 break-all">{entry.invoice_document}</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <motion.button
                        onClick={handleViewInvoice}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Ver factura"
                      >
                        <Eye className="w-4 h-4" />
                        Ver
                      </motion.button>
                      
                      <motion.button
                        onClick={handleDownloadInvoice}
                        className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Descargar factura"
                      >
                        <Download className="w-4 h-4" />
                        Descargar
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">No hay factura del material</p>
                <p className="text-sm">Haz clic en "Subir Factura" para agregar una</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Modal para subir documento */}
      <AnimatePresence>
        {showDocumentUploader && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {entry.requisition_document ? 'Cambiar' : 'Subir'} Documento de Requisición
                </h3>
                <DocumentUploader
                  onDocumentUpload={handleDocumentUpload}
                  currentDocument={entry.requisition_document}
                  onCancel={() => setShowDocumentUploader(false)}
                  type="entry"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal para ver imagen */}
      <AnimatePresence>
        {showImageViewer && entry.requisition_document && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowImageViewer(false)}
          >
            <motion.div
              className="relative max-w-4xl max-h-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imagesService.getImageUrl(entry.requisition_document)}
                alt="Documento de requisición"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
              <button
                onClick={() => setShowImageViewer(false)}
                className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
              >
                <X className="w-6 h-6" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal para subir factura */}
      <AnimatePresence>
        {showInvoiceUploader && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {entry.invoice_document ? 'Cambiar' : 'Subir'} Factura del Material
                </h3>
                <DocumentUploader
                  onDocumentUpload={handleInvoiceUpload}
                  currentDocument={entry.invoice_document}
                  onCancel={() => setShowInvoiceUploader(false)}
                  type="invoice"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal para ver factura */}
      <AnimatePresence>
        {showInvoiceViewer && entry.invoice_document && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowInvoiceViewer(false)}
          >
            <motion.div
              className="relative max-w-4xl max-h-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imagesService.getImageUrl(entry.invoice_document)}
                alt="Factura del material"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
              <button
                onClick={() => setShowInvoiceViewer(false)}
                className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
              >
                <X className="w-6 h-6" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de eliminación removido */}
    </motion.div>
  );
};

export default EntryDetailPage;
